import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import cameras from "../data/cameras";
import cameraTypes from "../data/camera-types.json";
import scenarios from "../data/scenarios.json";
import lightingConditions from "../data/lighting.json";
import movements from "../data/movement.json";
import looks from "../data/looks.json";
import outputs from "../data/output.json";
import curatedCombos from "../data/recipes-curated.json";

import { generateRecipe } from "../engine/ruleEngine";
import Footer from "../components/Footer";
import Icon from "../components/Icon";
import SceneAnalyzer from "../components/SceneAnalyzer";
import { QRCodeSVG } from "qrcode.react";
import { gradientForLighting } from "../utils/lightingGradients";
import { imageForLighting, altForLighting } from "../utils/lightingImages";
import { detectPlatform, canMeasure } from "../utils/platform";
import { isPaidUnlocked } from "../utils/paidUnlock";
import { PRICING } from "../config/pricing";
import { usePublished } from "../utils/publishing";
import { setPageMeta, breadcrumbLd } from "../utils/meta";

/**
 * Every recipe, grouped by what someone is actually trying to shoot.
 *
 * The presentation is a photo-card grid with search and category filters,
 * and a card opens a modal (static image on one side, scrolling settings
 * on the other) rather than navigating away — quicker to skim several
 * recipes on a phone. The full, crawlable, shareable page still lives at
 * /camera/:slug and the modal links straight to it.
 *
 * The crawling and scheduling guarantees are unchanged from the previous
 * version: every link is present in the DOM unconditionally (no accordion),
 * the list is filtered through usePublished so nothing scheduled leaks, and
 * the page still emits its own <head> and JSON-LD for prerender.
 *
 * Grouping is still by scenario CATEGORY — people arrive knowing what
 * they're filming more often than which body they want to read about, and
 * the camera hubs already cover the by-camera view.
 */

/* -------------------------------------------------------------- modal ----- */

/**
 * The modal's left panel. What it shows depends on device + purchase, per the
 * product rules:
 *
 *   Desktop            → static image + a QR code that opens /view/:slug on a
 *                        phone (the camera can't usefully run on desktop).
 *   Phone + unlocked   → the live SceneAnalyzer (camera) in the panel.
 *   Phone + NOT unlocked → static image with an "Unlock for $5" overlay.
 *   No camera / unknown  → static image (graceful fallback).
 *
 * SceneAnalyzer itself is cross-browser and degrades honestly (metered EV on
 * browsers that expose exposure, relative brightness elsewhere).
 */
function ModalImagePanel({ entry, recipe }) {
  const { camera, scenario, lighting, combo } = entry;
  const grad = gradientForLighting(lighting.id);

  const [platform, setPlatform] = useState("unknown");
  const [unlocked, setUnlocked] = useState(false);
  const [origin, setOrigin] = useState("");
  const [measuring, setMeasuring] = useState(false); // camera started?

  useEffect(() => {
    setPlatform(detectPlatform());
    setUnlocked(isPaidUnlocked());
    if (typeof window !== "undefined") setOrigin(window.location.origin);
  }, []);

  const isPhone = platform === "ios" || platform === "android";
  const cameraUsable = isPhone && canMeasure();

  const StaticImage = ({ children }) => (
    <div
      className={`modal-image text-${grad.text}`}
      style={{ "--grad-from": grad.from, "--grad-to": grad.to }}
    >
      <img src={imageForLighting(lighting.id)} alt={altForLighting(lighting.id, camera.name, scenario.name)} loading="lazy" onError={(e)=>{e.currentTarget.style.display="none";}} />
      <div className="modal-image-tint" aria-hidden="true" />
      <div className="modal-image-overlay modal-image-overlay-top">
        <span className="modal-image-camera">{camera.name}</span>
        <h3 id="recipe-modal-title">{scenario.name}</h3>
      </div>
      <span className="modal-image-light">
        <Icon name="light" size={13} /> {lighting.name}
      </span>
      {children}
    </div>
  );

  // Phone + paid → image with a "Measure the light" button; tapping it
  // swaps the image for the live analyzer with a smooth animated transition.
  if (cameraUsable && unlocked) {
    const analyzerRecipe = {
      ...recipe,
      lightingName: lighting.name,
      lightingExposureStops: lighting.exposureStops,
    };

    if (!measuring) {
      return (
        <StaticImage>
          <div className="modal-measure">
            <button
              type="button"
              className="modal-measure-btn"
              onClick={() => setMeasuring(true)}
            >
              <Icon name="light" size={16} /> Measure the light
            </button>
            <p className="modal-measure-hint">
              Point the camera at your scene to get the ND filter that actually fits.
            </p>
          </div>
        </StaticImage>
      );
    }
    return (
      <div
        className={`modal-image modal-image-analyzer modal-analyzer-enter text-${grad.text}`}
        style={{ "--grad-from": grad.from, "--grad-to": grad.to }}
      >
        {/* Keep a title for the dialog's aria-labelledby even in analyzer mode. */}
        <h3 id="recipe-modal-title" className="sr-only">
          {camera.name}: {scenario.name} scene analysis
        </h3>
        <div className="modal-analyzer-scroll">
          <SceneAnalyzer recipe={analyzerRecipe} camera={camera} />
        </div>
      </div>
    );
  }

  // Phone + NOT paid → static image with an unlock CTA.
  if (isPhone && !unlocked) {
    return (
      <StaticImage>
        <div className="modal-lock" role="note">
          <span className="modal-lock-badge"><Icon name="aperture" size={16} /> Scene Analysis</span>
          <p className="modal-lock-text">
            Measure the real light and pan speed with this phone's sensors, and get the ND filter
            that actually fits the scene in front of you.
          </p>
          <Link
            className="modal-lock-cta"
            to={`/pricing?from=${encodeURIComponent(`/camera/${combo.slug}`)}`}
          >
            Unlock for {PRICING.price}
          </Link>
        </div>
      </StaticImage>
    );
  }

  // Desktop → static image + QR to open on a phone.
  if (!isPhone && origin) {
    return (
      <StaticImage>
        <div className="modal-qr" aria-label="Scan to open on your phone">
          <QRCodeSVG value={`${origin}/gallery?open=${combo.slug}`} size={108} bgColor="transparent" fgColor="#16181a" level="M" />
        </div>
      </StaticImage>
    );
  }

  // Fallback (unknown platform / no camera): plain static image.
  return <StaticImage />;
}

function RecipeModal({ entry, onClose }) {
  const modalRef = useRef(null);
  const closeRef = useRef(null);
  const lastFocused = useRef(null);
  const [isPhone, setIsPhone] = useState(false);

  useEffect(() => {
    const pf = detectPlatform();
    setIsPhone(pf === "ios" || pf === "android");
  }, []);

  // Compute the full recipe live, from the same engine the recipe page uses —
  // settings for the preview table, and the whole object for the analyzer.
  const recipe = useMemo(() => {
    if (!entry) return null;
    const { camera, scenario, lighting } = entry;
    const movement = movements.find((m) => m.id === scenario.movementId);
    const look = looks.find((l) => l.id === entry.combo.lookId) || looks[0];
    const output = outputs.find((o) => o.id === (entry.combo.outputId || "youtube-long"));
    return generateRecipe({ camera, scenario, lighting, movement, look, output });
  }, [entry]);
  const settings = recipe ? recipe.settings : null;

  useEffect(() => {
    if (!entry) return undefined;
    lastFocused.current = document.activeElement;
    document.body.classList.add("modal-open");
    const id = setTimeout(() => closeRef.current?.focus(), 40);

    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const focusable = modalRef.current?.querySelectorAll(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable || !focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(id);
      document.body.classList.remove("modal-open");
      document.removeEventListener("keydown", onKey);
      lastFocused.current?.focus?.();
    };
  }, [entry, onClose]);

  if (!entry) return null;

  const { camera, scenario, lighting, combo } = entry;

  const rows = settings
    ? [
        ["Resolution", settings.resolution],
        ["Frame rate", `${settings.fps}fps`],
        ["Shutter", settings.shutter],
        ["ISO", settings.iso],
        ["White balance", settings.colorTemperature],
        ["Picture profile", settings.profile],
        ["ND filter", settings.ndFilter],
        ["Stabilization", settings.stabilization],
      ]
    : [];

  const onOverlayDown = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className="modal-overlay is-open" onMouseDown={onOverlayDown}>
      <div
        ref={modalRef}
        className="recipe-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="recipe-modal-title"
      >
        <ModalImagePanel entry={entry} recipe={recipe} />

        <div className="modal-content">
          <button ref={closeRef} className="modal-close" onClick={onClose} aria-label="Close">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
              <path d="M6 18 18 6M6 6l12 12" />
            </svg>
          </button>

          <p className="eyebrow">Recommended settings</p>
          {!isPhone && (
            <p className="modal-scan-note">
              Scan to measure the real light on your phone and get the ND filter that actually fits.
            </p>
          )}
          <h2 className="modal-title">
            {camera.name}: {scenario.name}
          </h2>
          <p className="modal-sub">{scenario.description}</p>

          {settings && (
            <table className="modal-settings">
              <tbody>
                {rows.map(([label, value]) => (
                  <tr key={label}>
                    <th scope="row">{label}</th>
                    <td className="data">{value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {settings?.ndNote && <p className="modal-note">{settings.ndNote}</p>}

          {combo.covers && combo.covers.length > 0 && (
            <p className="modal-covers">
              This recipe also answers {combo.covers.length} related{" "}
              {combo.covers.length === 1 ? "situation" : "situations"} that land on the same settings.
            </p>
          )}

          <div className="modal-actions">
            <Link to={`/camera/${combo.slug}`} className="modal-primary">
              Open the full recipe
            </Link>
            <Link to={`/cameras/${camera.id}`} className="modal-secondary">
              All {camera.name} settings
            </Link>
          </div>

          <p className="modal-foot">
            The full page shows the reasoning behind each setting, an FAQ, and a QR code to pull it up
            on your phone on set.
          </p>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------- gallery ---- */

export default function Gallery() {
  const { items: live } = usePublished(curatedCombos);
  const [searchParams, setSearchParams] = useSearchParams();

  const [category, setCategory] = useState("All");
  const [active, setActive] = useState(null); // entry for the open modal

  // Build the full list of {combo, scenario, camera, lighting} once.
  const enriched = useMemo(() => {
    const out = [];
    for (const combo of live) {
      const scenario = scenarios.find((s) => s.id === combo.scenarioId);
      const camera = cameras.find((c) => c.id === combo.cameraId);
      const lighting = lightingConditions.find((l) => l.id === combo.lightingId);
      if (!scenario || !camera || !lighting) continue;
      out.push({ combo, scenario, camera, lighting });
    }
    return out;
  }, [live]);

  // Auto-open a recipe's modal when arriving with ?open=<slug> — this is what
  // the scanned QR links to, so a phone lands on the exact same modal UI (with
  // the live camera in the image slot when paid) rather than a separate page.
  useEffect(() => {
    const slug = searchParams.get("open");
    if (!slug || !enriched.length) return;
    const entry = enriched.find((e) => e.combo.slug === slug);
    if (entry) setActive(entry);
  }, [searchParams, enriched]);

  // When the modal is closed, drop ?open= from the URL so it doesn't reopen.
  const closeModal = () => {
    setActive(null);
    if (searchParams.get("open")) {
      const next = new URLSearchParams(searchParams);
      next.delete("open");
      setSearchParams(next, { replace: true });
    }
  };

  const categories = useMemo(() => {
    const set = new Set(enriched.map((e) => e.scenario.category || "Other"));
    return ["All", ...[...set].sort()];
  }, [enriched]);

  const filtered = useMemo(() => {
    return enriched.filter(
      (e) => category === "All" || (e.scenario.category || "Other") === category
    );
  }, [enriched, category]);

  // One continuous, sorted grid — grouped visually by category label on each
  // card rather than split into separate half-empty sections.
  const sorted = useMemo(
    () =>
      [...filtered].sort(
        (a, b) =>
          (a.scenario.category || "Other").localeCompare(b.scenario.category || "Other") ||
          a.camera.name.localeCompare(b.camera.name) ||
          a.scenario.name.localeCompare(b.scenario.name)
      ),
    [filtered]
  );
  const total = enriched.length;

  const typeCounts = useMemo(() => {
    const cameraType = {};
    cameras.forEach((c) => {
      cameraType[c.id] = c.type;
    });
    const counts = {};
    live.forEach((combo) => {
      const t = cameraType[combo.cameraId];
      if (t) counts[t] = (counts[t] || 0) + 1;
    });
    return counts;
  }, [live]);

  const typesWithRecipes = cameraTypes
    .filter((t) => typeCounts[t.id] > 0)
    .sort((a, b) => typeCounts[b.id] - typeCounts[a.id]);

  useEffect(() => {
    setPageMeta({
      title: `All ${total} camera settings`,
      description: `Every published camera settings recipe, grouped by what you're shooting: action, cinematic, travel, drone and more.`,
      path: "/gallery/",
      jsonLdId: "gallery-jsonld",
      jsonLd: {
        "@context": "https://schema.org",
        "@graph": [
          { "@type": "CollectionPage", name: "Settings", description: "Every published camera settings recipe." },
          breadcrumbLd([{ name: "Home", path: "/" }, { name: "Settings", path: "/gallery/" }]),
        ],
      },
    });
  }, [total]);

  return (
    <main className="gallery-page">
      <nav className="breadcrumb" aria-label="Breadcrumb">
        <Link to="/">Home</Link> <span aria-hidden="true">/</span> <span>Settings</span>
      </nav>

      <header className="gallery-header">
        <p className="eyebrow">{total} published {total === 1 ? "recipe" : "recipes"}</p>
        <h1>Camera settings, by what you're shooting</h1>
        <p className="gallery-sub">
          Each one is a specific camera in a specific light. Open a card to see the settings, or the
          full page for the reasoning behind them.
        </p>
      </header>

      {typesWithRecipes.length > 0 && (
        <nav className="gallery-types-compact" aria-label="Browse by camera type">
          <span className="gallery-types-compact-label">By camera type</span>
          {typesWithRecipes.map((t) => (
            <Link key={t.id} to={`/gallery/${t.id}`} className="type-chip">
              {t.name} <span className="type-chip-count data">{typeCounts[t.id]}</span>
            </Link>
          ))}
        </nav>
      )}

      {/* Search + category filters. Purely client-side over data already in
          the bundle, so it works offline — same as the header search. */}
      <div className="gallery-tools">
        <div className="gallery-filters" role="group" aria-label="Filter by category">
          {categories.map((c) => (
            <button
              key={c}
              type="button"
              className={`gallery-filter ${category === c ? "is-active" : ""}`}
              onClick={() => setCategory(c)}
              aria-pressed={category === c}
            >
              {c}
            </button>
          ))}
        </div>

        <p className="gallery-count" aria-live="polite">
          Showing <strong className="data">{filtered.length}</strong> of{" "}
          <strong className="data">{total}</strong> recipes
        </p>
      </div>

      {total === 0 && (
        <p className="gallery-empty">
          No recipes are published yet. <Link to="/builder">Build one for any combination</Link> instead.
        </p>
      )}

      {total > 0 && filtered.length === 0 && (
        <div className="gallery-none">
          <h3>No recipes match that</h3>
          <p>
            Try a camera model, a scenario like "walking vlog", or a lighting condition. You can also{" "}
            <Link to="/builder">build one for any combination</Link>.
          </p>
        </div>
      )}

      {filtered.length > 0 && (
        <ul className="recipe-grid recipe-grid-all">
          {sorted.map((e) => {
            const grad = gradientForLighting(e.lighting.id);
            const covers = e.combo.covers ? e.combo.covers.length : 0;
            return (
              <li key={e.combo.slug}>
                <article
                  className="recipe-card"
                  role="button"
                  tabIndex={0}
                  aria-label={`View ${e.camera.name} settings for ${e.scenario.name} in ${e.lighting.name}`}
                  onClick={() => setActive(e)}
                  onKeyDown={(ev) => {
                    if (ev.key === "Enter" || ev.key === " ") {
                      ev.preventDefault();
                      setActive(e);
                    }
                  }}
                >
                  <div
                    className={`recipe-card-image text-${grad.text}`}
                    style={{ "--grad-from": grad.from, "--grad-to": grad.to }}
                  >
                    <img src={imageForLighting(e.lighting.id)} alt={altForLighting(e.lighting.id, e.camera.name, e.scenario.name)} loading="lazy" onError={(e)=>{e.currentTarget.style.display="none";}} />
                    <div className="recipe-card-tint" aria-hidden="true" />
                    {e.scenario.category && (
                      <span className="recipe-card-cat">{e.scenario.category}</span>
                    )}
                    <span className="recipe-card-light">
                      <Icon name="light" size={12} /> {e.lighting.name}
                    </span>
                  </div>

                  <div className="recipe-card-body">
                    <div>
                      <p className="recipe-card-camera">{e.camera.name}</p>
                      <h3 className="recipe-card-title">{e.scenario.name}</h3>
                      {covers > 0 && (
                        <p className="recipe-card-covers">
                          +{covers} more {covers === 1 ? "situation" : "situations"}
                        </p>
                      )}
                    </div>
                    <span className="recipe-card-arrow" aria-hidden="true">
                      <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M13.5 4.5 21 12l-7.5 7.5M21 12H3" />
                      </svg>
                    </span>
                  </div>
                </article>
              </li>
            );
          })}
        </ul>
      )}

      <div data-prerender-ready="true" style={{ display: "none" }} />
      <Footer />

      <RecipeModal entry={active} onClose={closeModal} />
    </main>
  );
}
